import { bffApi } from "../../autogen/bff-api";
import type { AgentToolDescriptor } from "./agentTypes";
import { currentValueAt } from "./confirmationCardFormatting";
import { isNotFound, PROCUREMENT_GONE, PROCUREMENT_GONE_RESULT } from "./procurementGone";
import { sourcingEventForModel } from "./sourcingEventForModel";
import { RULES_NOW, storedRules } from "./storedRules";
import { toError } from "./tools";
import type { ToolContext, ToolResult } from "./tools";

type Refused = Extract<ToolResult, { ok: false }>;
type Row = Record<string, unknown>;

const readSourcingEvent = (args: Row, ctx: ToolContext): Promise<unknown> =>
  ctx
    .dispatch(bffApi.endpoints.getSourcingEvent.initiate({ eventId: String(args.eventId ?? "") }, { forceRefetch: true }))
    .unwrap();

const CURRENT_READERS: Record<string, (args: Row, ctx: ToolContext) => Promise<unknown>> = {
  updateSourcingEvent: readSourcingEvent,
};

export const readStoredEvent = async (args: Row, ctx: ToolContext): Promise<Row | undefined> => {
  try {
    const event = await readSourcingEvent(args, ctx);
    return isRow(event) ? event : undefined;
  } catch {
    return undefined;
  }
};


export const readCurrent = async (callName: string, args: Row, ctx: ToolContext): Promise<ToolResult | null> => {
  const read = CURRENT_READERS[callName];
  if (!read) return null;
  try {
    return { ok: true, data: await read(args, ctx) };
  } catch (err) {
    return isNotFound(err) ? PROCUREMENT_GONE_RESULT : unreadable(toError(err));
  }
};

const unreadable = (failed: ToolResult): ToolResult =>
  failed.ok
    ? failed
    : {
        ...failed,
        message:
          "Anskaffelsen kunne ikke leses akkurat nå, så det er ikke vist noe forslag og ingenting er lagret. " +
          "Si det til brukeren, beskriv ikke anskaffelsen, og be brukeren spørre igjen om litt.",
      };

export const withExpectedCurrent = (descriptor: AgentToolDescriptor, args: Row, current: Row): Row => {
  const body = descriptor.params.find((param) => param.in === "body")?.name;
  const patch = body ? args[body] : undefined;
  if (!body || !isRow(patch)) return args;
  const expectedCurrent = touchedPaths(patch, []).map((path) => ({
    path: path.join("."),
    value: JSON.stringify(currentValueAt(current, path) ?? null),
  }));
  return { ...args, [body]: { ...patch, expectedCurrent } };
};

export const withStateAfterRefusal = async (
  callName: string,
  descriptor: AgentToolDescriptor,
  sent: Row,
  refused: Refused,
  ctx: ToolContext,
): Promise<Refused> => {
  const after = await readCurrent(callName, sent, ctx);
  if (!after) return refused;
  if (!after.ok) return after.deviationCode === PROCUREMENT_GONE ? after : withoutCurrentState(refused);
  const now = sourcingEventForModel(after.data);
  const stored = refused.storedAtRefusal;
  const fields = cardFieldsNow(descriptor, sent, isRow(now) ? now : {}, stored);
  const readIsBehind = stored !== undefined && isRow(after.data) && disagrees(after.data, stored);
  const stale = refused.deviationCode === CHANGED_SINCE_PROPOSED;
  const rules = stale ? storedRules(stored, readIsBehind ? undefined : after.data) : undefined;
  const message = !stale
    ? refused.message
    : fields.length > 0 && fields.every((field) => field.alreadyAsProposed)
      ? ALREADY_AS_PROPOSED
      : `${refused.message} ${OUTDATED_BEFORE}`;
  return {
    ok: false,
    deviationCode: refused.deviationCode,
    message: rules ? `${message} ${RULES_NOW}` : message,
    cardFieldsNow: fields,
    ...(rules ? { storedRules: rules } : {}),
    ...(readIsBehind ? {} : { current: now }),
  };
};

export type ChangedSinceRead = { field: string; readEarlier: unknown; storedNow: unknown };

const CLASSIFYING_FIELDS = ["regulation", "foaPart", "estimatedValue", "contractNature", "procedureType"].map((field) => [
  "eventContext",
  field,
]);

export const changedSinceRead = (lastRead: Row | undefined, now: Row): ChangedSinceRead[] => {
  if (!lastRead || !isRow(lastRead.eventContext) || !isRow(now.eventContext)) return [];
  return CLASSIFYING_FIELDS.flatMap((path) => {
    const readEarlier = currentValueAt(lastRead, path) ?? null;
    const storedNow = currentValueAt(now, path) ?? null;
    return sameJson(readEarlier, storedNow) ? [] : [{ field: path.join("."), readEarlier, storedNow }];
  });
};

export const withChangesSinceRead = (refused: Refused, lastRead: Row | undefined, now: Row): Refused => {
  const changed = changedSinceRead(lastRead, now);
  if (changed.length === 0) return refused;
  const rules = storedRules(undefined, now);
  return {
    ...refused,
    message: `${refused.message} ${CHANGED_SINCE_READ}${rules ? ` ${RULES_NOW}` : ""}`,
    changedSinceYourLastRead: changed,
    ...(rules ? { storedRules: rules } : {}),
    current: sourcingEventForModel(now),
  };
};

const CHANGED_SINCE_READ =
  "Anskaffelsen er endret et annet sted siden du sist leste den i denne samtalen: «changedSinceYourLastRead» viser " +
  "hva du leste («readEarlier») og hva som er lagret nå («storedNow»). Grunnen over gjelder anskaffelsen slik den " +
  "er lagret nå («current»), ikke slik du leste den. Gjenta ikke del, terskel, verdi eller paragraf fra det du " +
  "leste tidligere, og si ikke at det skyldes en klassifisering som ikke er bekreftet. Si først hva som er lagret " +
  "nå, for eksempel verdien og delen, og forklar og foreslå bare ut fra det. Et nytt forslag nevner bare delen og " +
  "verdiene som er lagret nå.";

const OUTDATED_BEFORE =
  "«før»-verdiene på kortet, det du leste om anskaffelsen tidligere i denne meldingen og det du skrev før kortet " +
  "er utdatert. Slik feltene på kortet er lagret nå, står i «cardFieldsNow» (feltet «now»): beskriv dem bare ut " +
  "fra det.";

const ALREADY_AS_PROPOSED =
  "Anskaffelsen ble endret et annet sted etter at kortet ble laget, og feltene på kortet er nå allerede lagret " +
  "slik kortet foreslo («cardFieldsNow»). Si til brukeren at verdiene de ba om er lagret, at det skjedde et annet " +
  "sted, og at kortet derfor ikke trengte å lagre noe. Si IKKE at verdiene ikke ble lagret, og tilby ikke et nytt " +
  "kort for dem.";

type CardFieldNow = { field: string; now: unknown; proposed: unknown; alreadyAsProposed: boolean };

const disagrees = (read: Row, stored: Record<string, unknown>): boolean =>
  Object.entries(stored).some(([field, value]) => !sameJson(currentValueAt(read, field.split(".")), value));

const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const cardFieldsNow = (
  descriptor: AgentToolDescriptor,
  sent: Row,
  now: Row,
  stored: Record<string, unknown> | undefined,
): CardFieldNow[] => {
  const body = descriptor.params.find((param) => param.in === "body")?.name;
  const patch = body ? sent[body] : undefined;
  if (!isRow(patch)) return [];
  return touchedPaths(patch, []).map((path) => {
    const field = path.join(".");
    const proposed = valueAt(patch, path) ?? null;
    const atNow = (stored && field in stored ? stored[field] : currentValueAt(now, path)) ?? null;
    return {
      field,
      now: atNow,
      proposed,
      alreadyAsProposed: holds(atNow, proposed),
    };
  });
};

const valueAt = (node: Row, path: string[]): unknown =>
  path.reduce<unknown>((at, key) => (isRow(at) ? at[key] : undefined), node);

const holds = (stored: unknown, proposed: unknown): boolean => {
  if (proposed === null || typeof proposed !== "object") return stored === proposed;
  if (Array.isArray(proposed)) {
    return Array.isArray(stored) && stored.length === proposed.length && proposed.every((item, i) => holds(stored[i], item));
  }
  return isRow(stored) && Object.entries(proposed).every(([key, value]) => holds(stored[key] ?? null, value));
};

const CHANGED_SINCE_PROPOSED = "zz_FieldErrorCode_ChangedSinceProposed";

const withoutCurrentState = (refused: Refused): Refused =>
  refused.deviationCode === CHANGED_SINCE_PROPOSED
    ? {
        ...refused,
        message:
          "Anskaffelsen er endret et annet sted etter at kortet ble laget, og slik den er nå, kunne ikke leses. " +
          "Beskriv IKKE anskaffelsen, heller ikke ut fra det du leste før kortet. Be brukeren laste siden på nytt " +
          "eller spørre igjen, så lager du et nytt forslag ut fra slik den er da.",
      }
    : refused;

const NOT_CHANGES = new Set(["fieldSources", "expectedCurrent"]);

const touchedPaths = (node: Row, at: string[]): string[][] =>
  Object.entries(node)
    .filter(([key, value]) => value !== undefined && !(at.length === 0 && NOT_CHANGES.has(key)))
    .flatMap(([key, value]) =>
      isRow(value) && Object.keys(value).length > 0 ? touchedPaths(value, [...at, key]) : [[...at, key]],
    );

const isRow = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);
